import type { ScenarioRow } from './types.js';

/**
 * Serialise a list of scenarios to RFC 4180 CSV. Mirrors the legacy
 * `wf-audit/src/slices/scenario/utils/exportScenarios.ts` with three
 * pragmatic upgrades:
 *
 *   1. Explicit column list instead of "every primitive key the first
 *      row happens to have". Stable column order across runs +
 *      independent of how the engine response is shaped this week.
 *   2. Always-quoted values when they contain `,`, `"`, `\n`, or
 *      `\r`. Embedded double-quotes are doubled per RFC 4180.
 *   3. UTF-8 BOM prefix so Excel opens the file without garbling
 *     accents (the most common operator-side complaint with CSV
 *     downloads from web apps).
 *
 * The caller resolves the filtered set and passes only the rows the
 * operator is currently looking at — clicking "Export CSV" with an
 * "Active" tab + a folder filter applied should produce a CSV of
 * exactly those rows, not the unfiltered license set.
 */
const COLUMNS: ReadonlyArray<{ key: keyof ScenarioRow; header: string }> = [
  { key: 'fusionId', header: 'Fusion ID' },
  { key: 'name', header: 'Name' },
  { key: 'description', header: 'Description' },
  { key: 'status', header: 'Status' },
  { key: 'folderName', header: 'Folder' },
  { key: 'teamFusionId', header: 'Team ID' },
  { key: 'totalModules', header: 'Modules' },
  { key: 'kpiCount', header: 'KPIs' },
  { key: 'modifiedBy', header: 'Modified By' },
  { key: 'modifiedAt', header: 'Modified At' },
  { key: 'lastSuccessfulRunAt', header: 'Last Run At' },
  { key: 'lastSuccessfulRunDuration', header: 'Last Run Duration (ms)' },
  { key: 'lastSuccessfulRunOperations', header: 'Last Run Operations' },
  { key: 'url', header: 'Fusion URL' },
];

function escapeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'string' ? value : String(value);
  if (/[",\r\n]/.test(raw)) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
}

export function scenariosToCsv(scenarios: ReadonlyArray<ScenarioRow>): string {
  const header = COLUMNS.map((c) => escapeCsvCell(c.header)).join(',');
  const lines = scenarios.map((s) =>
    COLUMNS.map((c) => escapeCsvCell(s[c.key] as unknown)).join(','),
  );
  // BOM + CRLF line endings — both Excel-friendly. Excel sniffs the
  // BOM to pick UTF-8; CRLF matches RFC 4180.
  return '﻿' + [header, ...lines].join('\r\n');
}

/**
 * Trigger a browser download of the CSV text under `filename`.
 * Side-effect-only — pure CSV serialisation lives in
 * {@link scenariosToCsv} so unit tests don't need a DOM.
 */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}
